import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { logError } from "../lib/log";
import { Flame, Trophy, Calendar, ArrowRight, BookOpen, Check, Mail, AlertCircle, Award, Lock } from "lucide-react";

function GoalEditor({ target, setTarget, onSave, saved, onCancel }) {
  return (
    <div className="mt-4 flex items-center gap-3">
      <input type="number" min={1} max={50} value={target} onChange={(e) => setTarget(Math.max(1, Math.min(50, Number(e.target.value) || 1)))} data-testid="goal-input"
        className="w-24 rounded-md border border-white/10 bg-black/60 px-3 py-2 text-lg font-mono-num text-white focus:border-cyan-400/60 focus:outline-none" />
      <span className="text-sm text-slate-400">lessons / week</span>
      <button onClick={onSave} data-testid="goal-save" className="rounded-md bg-cyan-500 px-4 py-2 text-sm font-semibold text-black hover:bg-cyan-400">
        {saved ? <><Check size={12} className="mr-1 inline" /> Saved</> : "Save"}
      </button>
      {onCancel && <button onClick={onCancel} className="text-xs text-slate-400 hover:text-slate-200">Cancel</button>}
    </div>
  );
}

const goalTitle = (g, done) => {
  if (!g.weekly_target) return "Set a target for this week";
  return done ? "🔥 Goal hit — nice work" : `Read ${g.weekly_target} lessons this week`;
};

function useWeeklyGoal() {
  const [g, setG] = useState(null);
  const [target, setTarget] = useState(3);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const load = () => api.get("/me/goal").then((r) => { setG(r.data); if (r.data.weekly_target) setTarget(r.data.weekly_target); }).catch((e) => logError("Goal load failed", e));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/me/goal", { weekly_target: target });
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
      load();
      return true;
    } catch (e) {
      setErr(apiError(e, "Could not save goal"));
      return false;
    }
  };
  return { g, target, setTarget, saved, err, save };
}

function GoalProgress({ g, done, onEdit }) {
  const pct = Math.min(100, Math.round((g.reads_this_week / g.weekly_target) * 100));
  return (
    <div className="mt-4">
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/5"><div className={`h-full ${done ? "bg-lime-400" : "bg-cyan-400"}`} style={{ width: `${pct}%` }} /></div>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span>{g.days_left} day{g.days_left !== 1 ? "s" : ""} left this week</span>
        <button onClick={onEdit} className="text-cyan-300 hover:underline" data-testid="goal-edit">Change target</button>
      </div>
    </div>
  );
}

export function WeeklyGoal() {
  const { g, target, setTarget, saved, err, save } = useWeeklyGoal();
  const [editing, setEditing] = useState(false);
  const onSave = async () => { if (await save()) setEditing(false); };
  if (!g) return null;
  const done = g.weekly_target && g.reads_this_week >= g.weekly_target;

  return (
    <section className="rounded-md border border-white/10 border-t-2 border-t-cyan-400 bg-black/40 p-6" data-testid="weekly-goal">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300">Weekly goal</div>
          <h2 className="font-display mt-1 text-xl font-semibold text-white">{goalTitle(g, done)}</h2>
        </div>
        {g.weekly_target ? <span className="font-mono-num rounded-md border border-cyan-400/40 bg-cyan-500/10 px-3 py-1 text-sm text-cyan-200" data-testid="goal-progress">{g.reads_this_week}/{g.weekly_target}</span> : null}
      </div>
      {g.weekly_target && !editing && <GoalProgress g={g} done={done} onEdit={() => setEditing(true)} />}
      {(editing || !g.weekly_target) && <GoalEditor target={target} setTarget={setTarget} onSave={onSave} saved={saved} onCancel={g.weekly_target ? () => setEditing(false) : null} />}
      {err && <div className="mt-2 text-xs text-red-300" data-testid="goal-error">{err}</div>}
    </section>
  );
}

export function JoinClassBanner() {
  const [state, setState] = useState({ loaded: false });
  const [code, setCode] = useState("");
  const [err, setErr] = useState(null);
  const load = () => api.get("/me/class").then((r) => setState({ loaded: true, ...r.data })).catch((e) => logError("Class load failed", e));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setErr(null);
    try {
      await api.post("/me/join-class", { code });
      load();
      setCode("");
    } catch (e) {
      setErr(apiError(e, "Invalid code"));
    }
  };

  if (!state.loaded) return null;
  if (state.joined) {
    return <div className="rounded-md border border-lime-400/40 bg-lime-500/5 p-4 text-sm" data-testid="joined-class"><span className="text-lime-200">✓ In {state.teacher_name}'s class ({state.class_code})</span></div>;
  }
  return (
    <div className="rounded-md border border-amber-400/40 bg-amber-500/5 p-5" data-testid="join-class-banner">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300">Class code</div>
          <div className="font-display mt-1 text-lg font-semibold text-white">Join your teacher's class</div>
          <div className="mt-1 text-xs text-slate-400">Ask your teacher for their 6-character code.</div>
        </div>
        <div className="flex gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={6} data-testid="join-class-input"
            className="font-mono-num w-32 rounded-md border border-white/10 bg-black/60 px-3 py-2 text-center text-lg tracking-widest text-amber-200 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none" />
          <button onClick={submit} disabled={code.length < 6} data-testid="join-class-btn" className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-black hover:bg-amber-400 disabled:opacity-50">Join</button>
        </div>
      </div>
      {err && <div className="mt-3 text-xs text-red-300" data-testid="join-class-error">{err}</div>}
    </div>
  );
}

function DigestBody({ d }) {
  return (
    <div className="mt-3 leading-relaxed">
      Hi {d.user.name?.split(" ")[0]},<br /><br />
      {!d.weekly_target && <>You haven't set a weekly goal yet — try 3 lessons/week to build the habit.</>}
      {d.weekly_target && d.goal_hit && <>You hit your goal of <b>{d.weekly_target} lesson(s)</b> this week — nicely done. 🔥</>}
      {d.weekly_target && !d.goal_hit && <>You've read <b>{d.reads_this_week}/{d.weekly_target}</b> lessons this week. A quick session today would get you there.</>}
      <br /><br />
      {d.mistakes_due > 0 ? (
        <>You have <b>{d.mistakes_due}</b> question{d.mistakes_due !== 1 ? "s" : ""} due for review:
          <ul className="mt-2 list-disc pl-6">{d.top_mistakes.map((m) => <li key={m.id} className="mt-1">{m.q}</li>)}</ul>
        </>
      ) : <>Your review queue is empty — you're on top of things.</>}
    </div>
  );
}

export function DigestPreview() {
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  useEffect(() => { api.get("/me/digest").then((r) => setD(r.data)).catch((e) => logError("Digest load failed", e)); }, []);
  if (!d) return null;
  const sendNow = async () => {
    setSending(true);
    setSent(null);
    try {
      await api.post("/me/digest/send");
      setSent("✓ Sent to your inbox");
    } catch (e) {
      setSent(apiError(e, "Send failed"));
    } finally {
      setSending(false);
    }
  };
  return (
    <details className="rounded-md border border-white/10 bg-black/40 p-5" data-testid="digest-preview">
      <summary className="cursor-pointer text-sm text-slate-300 hover:text-cyan-300">📬 Preview your Sunday digest email</summary>
      <div className="mt-4 rounded-md border border-white/10 bg-black/60 p-5 font-sans text-sm text-slate-300">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">From ChemLab · to {d.user.email}</div>
        <div className="font-display mt-2 text-lg text-white">Your ChemLab week</div>
        <DigestBody d={d} />
        {d.note && <div className="mt-4 rounded-md border border-amber-400/30 bg-amber-500/5 p-2 text-[10px] text-amber-200">{d.note} · Auto-sent every Sunday morning UTC.</div>}
        <button onClick={sendNow} disabled={sending} data-testid="digest-send-btn" className="mt-3 inline-flex items-center gap-2 rounded-md border border-cyan-400/40 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200 hover:bg-cyan-500/15 disabled:opacity-50">
          <Mail size={12} /> {sending ? "Sending…" : "Send test to my inbox"}
        </button>
        {sent && <div className="mt-2 text-xs text-slate-400" data-testid="digest-sent-status">{sent}</div>}
      </div>
    </details>
  );
}

export function StatCards({ progress }) {
  const cards = [
    { tid: "stat-streak", label: "Streak", full: "Current streak", Icon: Flame, border: "border-t-orange-400", icon: "text-orange-400", value: progress?.streak_days ?? 0, unit: "days" },
    { tid: "stat-attempts", label: "Quizzes", full: "Quiz attempts", Icon: Calendar, border: "border-t-cyan-400", icon: "text-cyan-400", value: progress?.attempts_count ?? 0 },
    { tid: "stat-topics", label: "Topics", full: "Topics practiced", Icon: Trophy, border: "border-t-lime-400", icon: "text-lime-400", value: progress?.best_by_topic?.length ?? 0 },
  ];
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-4">
      {cards.map(({ tid, label, full, Icon, border, icon, value, unit }) => (
        <div key={tid} className={`rounded-md border border-white/10 border-t-2 bg-black/40 p-3 sm:p-6 ${border}`} data-testid={tid}>
          <div className="flex items-center justify-between gap-1">
            <div className="truncate text-[10px] uppercase tracking-widest text-slate-500"><span className="sm:hidden">{label}</span><span className="hidden sm:inline">{full}</span></div>
            <Icon className={`shrink-0 ${icon}`} size={16} />
          </div>
          <div className="font-mono-num mt-2 text-2xl font-bold text-white sm:mt-4 sm:text-4xl">{value}{unit && <span className="text-xs text-slate-500 sm:text-lg"> {unit}</span>}</div>
        </div>
      ))}
    </div>
  );
}

export function TopicBests({ topics, progress }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold">Your best per topic</h2>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {topics.map((t) => {
          const best = progress?.best_by_topic?.find((b) => b.topic_id === t.id);
          const pct = best ? Math.round(best.pct * 100) : 0;
          return (
            <Link key={t.id} to={`/quiz/${t.id}`} data-testid={`dash-topic-${t.id}`} className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-black/40 p-4 hover:border-cyan-400/40">
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-white">{t.title}</div>
                <div className="mt-1 h-1.5 max-w-56 overflow-hidden rounded-full bg-white/5"><div className="h-full bg-cyan-400" style={{ width: `${pct}%` }} /></div>
              </div>
              <div className="shrink-0 text-right">
                <div className="font-mono-num text-lg text-white">{best ? `${best.score}/${best.total}` : "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{best ? `${pct}%` : "Not tried"}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export function RecentActivity({ progress, topicMap }) {
  const recent = (progress?.recent || []).slice(0, 8);
  return (
    <section>
      <h2 className="font-display text-2xl font-bold">Recent activity</h2>
      <div className="mt-4 divide-y divide-white/5 rounded-md border border-white/10 bg-black/40">
        {recent.map((r, i) => (
          <div key={r.id || `${r.topic_id}-${r.created_at}`} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`recent-${i}`}>
            <div className="flex items-center gap-3"><BookOpen size={14} className="text-slate-400" /><span className="text-slate-200">{topicMap[r.topic_id]?.title || r.topic_id}</span></div>
            <div className="flex items-center gap-4"><span className="font-mono-num text-slate-300">{r.score}/{r.total}</span><span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span></div>
          </div>
        ))}
        {recent.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">No attempts yet. <Link to="/quiz" className="text-cyan-300 hover:underline">Start a quiz <ArrowRight size={12} className="inline" /></Link></div>
        )}
      </div>
    </section>
  );
}

export function MistakesCta({ count }) {
  if (!count) return null;
  return (
    <Link to="/mistakes" data-testid="mistakes-cta" className="flex items-center justify-between rounded-md border border-red-400/40 bg-red-500/10 p-5 hover:border-red-400/60">
      <div className="flex items-center gap-3">
        <AlertCircle className="text-red-300" size={20} />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-300">Practice mistakes</div>
          <div className="font-display mt-1 text-lg font-semibold text-white">{count} question{count !== 1 ? "s" : ""} to review</div>
        </div>
      </div>
      <div className="font-mono-num text-2xl text-red-300">→</div>
    </Link>
  );
}

const TIER_TONE = { 7: "text-cyan-300 border-cyan-400/50 bg-cyan-500/10", 30: "text-fuchsia-300 border-fuchsia-400/50 bg-fuchsia-500/10", 100: "text-amber-300 border-amber-400/50 bg-amber-500/10" };

export function StreakBadges() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/me/badges").then((r) => setB(r.data)).catch((e) => logError("Badges load failed", e)); }, []);
  if (!b) return null;
  return (
    <section className="rounded-md border border-white/10 bg-black/40 p-5 sm:p-6" data-testid="streak-badges">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-300"><Award size={12} /> Streak badges</div>
          <h2 className="font-display mt-1 text-xl font-semibold text-white">
            <span className="font-mono-num" data-testid="badge-current-streak">{b.current_streak}</span>-day daily-challenge streak
            <span className="ml-2 text-sm font-normal text-slate-500">· best <span className="font-mono-num" data-testid="badge-best-streak">{b.best_streak}</span></span>
          </h2>
        </div>
        {b.next && <div className="text-xs text-slate-400" data-testid="badge-next"><span className="font-mono-num text-white">{b.next.remaining}</span> more day{b.next.remaining === 1 ? "" : "s"} to <b className="text-slate-200">{b.next.name}</b></div>}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        {b.badges.map((t) => (
          <div key={t.days} data-testid={`badge-${t.days}`} data-earned={t.earned} title={t.blurb}
            className={`flex flex-col items-center rounded-md border p-3 text-center transition-colors ${t.earned ? TIER_TONE[t.days] : "border-white/10 bg-white/[0.03] text-slate-500"}`}>
            <div className={`grid h-10 w-10 place-items-center rounded-full border ${t.earned ? "border-current" : "border-white/10"}`}>
              {t.earned ? <Flame size={18} /> : <Lock size={14} />}
            </div>
            <div className="font-mono-num mt-2 text-lg font-bold">{t.days}</div>
            <div className={`text-[10px] uppercase tracking-widest ${t.earned ? "" : "text-slate-600"}`}>{t.name}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
